import { z } from 'zod';
import { personNameSchema } from './accounts.js';

/** BR-12: wiek dziecka liczony na rok edycji; dziecko 3–18 lat. */
export const CHILD_MIN_AGE = 3;
export const CHILD_MAX_AGE = 18;

/** Z-401: przy dodaniu dziecka dopuszczamy 2–18 lat w bieżącym roku (3–18 sprawdza zgłoszenie do edycji). */
export const CHILD_MIN_AGE_ON_CREATE = 2;

export function ageInYear(birthYear: number, year: number): number {
  return year - birthYear;
}

export function isChildAgeAllowedForEdition(birthYear: number, editionYear: number): boolean {
  const age = ageInYear(birthYear, editionYear);
  return age >= CHILD_MIN_AGE && age <= CHILD_MAX_AGE;
}

export function isBirthYearAllowedOnCreate(birthYear: number, currentYear: number): boolean {
  const age = ageInYear(birthYear, currentYear);
  return age >= CHILD_MIN_AGE_ON_CREATE && age <= CHILD_MAX_AGE;
}

/** Zakres roku urodzenia dziecka dopuszczony w danym roku (US-402 AC-2) – do komunikatów i pól formularza. */
export function birthYearRange(currentYear: number): { min: number; max: number } {
  return { min: currentYear - CHILD_MAX_AGE, max: currentYear - CHILD_MIN_AGE_ON_CREATE };
}

export const participantKinds = ['child', 'adult'] as const;
export const participantKindSchema = z.enum(participantKinds);
export type ParticipantKind = z.infer<typeof participantKindSchema>;

/**
 * US-402 AC-1, US-403 AC-1: dane uczestnika. Rok urodzenia tylko dla dziecka i wymagany. Zakres lat (AC-2) sprawdza serwer
 * względem bieżącego roku w strefie wydarzenia – formularz sprawdza to samo przez `birthYearRange`.
 */
export const participantInputSchema = z
  .object({
    firstName: personNameSchema,
    lastName: personNameSchema,
    kind: participantKindSchema,
    // Pole formularza daje tekst; pusty = brak roku (wtedy dla dziecka komunikat z refine niżej).
    birthYear: z.preprocess(
      (value) => (value === '' || value === null || value === undefined ? null : Number(value)),
      z
        .number({ message: 'Podaj rok, np. 2016.' })
        .int({ message: 'Podaj rok, np. 2016.' })
        .min(1900, { message: 'Podaj rok, np. 2016.' })
        .max(2200, { message: 'Podaj rok, np. 2016.' })
        .nullable(),
    ),
  })
  .refine((value) => value.kind !== 'child' || typeof value.birthYear === 'number', {
    path: ['birthYear'],
    message: 'Podaj rok urodzenia dziecka.',
  })
  .transform((value) => ({ ...value, birthYear: value.kind === 'child' ? (value.birthYear ?? null) : null }));
export type ParticipantInput = z.infer<typeof participantInputSchema>;

/** Nadchodząca edycja, do której osoba jest zgłoszona (US-401 AC-2). */
export const participantEditionSchema = z.object({ slug: z.string(), name: z.string(), city: z.string(), date: z.string() });

export const participantSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  kind: participantKindSchema,
  birthYear: z.number().int().nullable(),
  /** Uczestnik opiekuna („Ty”, BR-11) – nie da się go usunąć ani zmienić typu. */
  isGuardian: z.boolean(),
  upcomingEditions: z.array(participantEditionSchema),
  /** US-407 AC-4: ostatnią zmianę zrobiła obsługa. */
  changedByStaff: z.boolean(),
});
export type Participant = z.infer<typeof participantSchema>;
export const participantListSchema = z.object({ participants: z.array(participantSchema) });

/** Konflikt przy zmianie danych uczestnika (US-403 AC-2, AC-3). */
export const participantConflictSchema = z.object({ edition: z.string() });
